import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import Layout from '../../component/layout';
import { toastify } from '../../../../utils/toast';
import { getItem } from '../../../../utils/localStorage';
import { makeRequest2 } from '../../../../utils/makeRequest';
import {
  get_handover_summary_report,
  get_inventory_comparison_report,
  get_meter_readings_report,
  get_handover_completion_report
} from '../../../../utils/urls';

function HandoverReports() {
  // State for each report tab
  const [summaryData, setSummaryData] = useState([]);
  const [inventoryData, setInventoryData] = useState([]);
  const [meterData, setMeterData] = useState([]);
  const [completionData, setCompletionData] = useState([]);

  // Loading states
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [loadingMeter, setLoadingMeter] = useState(false);
  const [loadingCompletion, setLoadingCompletion] = useState(false);

  // Filter states for Handover Summary
  const [summaryFilters, setSummaryFilters] = useState({
    search: '',
    handoverType: 'All Types',
    status: 'All Status',
    startDate: '',
    endDate: ''
  });

  // Filter states for Inventory Comparison
  const [inventoryFilters, setInventoryFilters] = useState({
    search: '',
    conditionChange: 'All Changes',
    category: 'All Categories'
  });

  // Filter states for Meter Readings
  const [meterFilters, setMeterFilters] = useState({
    search: '',
    meterType: 'All Types',
    startDate: '',
    endDate: ''
  });

  // Filter states for Completion
  const [completionFilters, setCompletionFilters] = useState({
    search: '',
    period: 'Last 30 Days',
    status: 'All Status'
  });

  // Summary states
  const [summarySummary, setSummarySummary] = useState({
    totalHandovers: 0,
    moveInCount: 0,
    moveOutCount: 0,
    completedCount: 0
  });

  const [inventorySummary, setInventorySummary] = useState({
    totalComparisons: 0,
    damaged: 0,
    missing: 0,
    totalCost: 0
  });

  const [meterSummary, setMeterSummary] = useState({
    totalReadings: 0,
    electricity: 0,
    water: 0,
    gas: 0
  });

  const [completionSummary, setCompletionSummary] = useState({
    completed: 0,
    draft: 0,
    avgCompletionTime: 0,
    signedCount: 0
  });

  // Fetch Handover Summary Report
  const fetchHandoverSummary = async () => {
    try {
      setLoadingSummary(true);
      const facilityId = await getItem('selectedFacilityId');
      const params = new URLSearchParams();
      if (summaryFilters.search) params.append('search', summaryFilters.search);
      if (summaryFilters.handoverType !== 'All Types') params.append('handoverType', summaryFilters.handoverType);
      if (summaryFilters.status !== 'All Status') params.append('status', summaryFilters.status);
      if (summaryFilters.startDate) params.append('startDate', summaryFilters.startDate);
      if (summaryFilters.endDate) params.append('endDate', summaryFilters.endDate);

      const response = await makeRequest2(
        `${get_handover_summary_report}/${facilityId}?${params.toString()}`,
        'GET',
        {}
      );

      if (response.success) {
        setSummaryData(response.data.data.handovers || []);
        setSummarySummary(response.data.data.summary || {
          totalHandovers: 0,
          moveInCount: 0,
          moveOutCount: 0,
          completedCount: 0
        });
      } else {
        throw new Error(response.message || 'Failed to fetch handover summary report');
      }
    } catch (error) {
      console.log(error.message);
      toastify(error.message, 'error');
    } finally {
      setLoadingSummary(false);
    }
  };

  // Fetch Inventory Comparison Report
  const fetchInventoryComparison = async () => {
    try {
      setLoadingInventory(true);
      const facilityId = await getItem('selectedFacilityId');
      const params = new URLSearchParams();
      if (inventoryFilters.search) params.append('search', inventoryFilters.search);
      if (inventoryFilters.conditionChange !== 'All Changes') params.append('conditionChange', inventoryFilters.conditionChange);
      if (inventoryFilters.category !== 'All Categories') params.append('category', inventoryFilters.category);

      const response = await makeRequest2(
        `${get_inventory_comparison_report}/${facilityId}?${params.toString()}`,
        'GET',
        {}
      );

      if (response.success) {
        setInventoryData(response.data.data.comparisons || []);
        setInventorySummary(response.data.data.summary || {
          totalComparisons: 0,
          damaged: 0,
          missing: 0,
          totalCost: 0
        });
      } else {
        throw new Error(response.message || 'Failed to fetch inventory comparison report');
      }
    } catch (error) {
      console.log(error.message);
      toastify(error.message, 'error');
    } finally {
      setLoadingInventory(false);
    }
  };

  // Fetch Meter Readings Report
  const fetchMeterReadings = async () => {
    try {
      setLoadingMeter(true);
      const facilityId = await getItem('selectedFacilityId');
      const params = new URLSearchParams();
      if (meterFilters.search) params.append('search', meterFilters.search);
      if (meterFilters.meterType !== 'All Types') params.append('meterType', meterFilters.meterType);
      if (meterFilters.startDate) params.append('startDate', meterFilters.startDate);
      if (meterFilters.endDate) params.append('endDate', meterFilters.endDate);

      const response = await makeRequest2(
        `${get_meter_readings_report}/${facilityId}?${params.toString()}`,
        'GET',
        {}
      );

      if (response.success) {
        setMeterData(response.data.data.readings || []);
        setMeterSummary(response.data.data.summary || {
          totalReadings: 0,
          electricity: 0,
          water: 0,
          gas: 0
        });
      } else {
        throw new Error(response.message || 'Failed to fetch meter readings report');
      }
    } catch (error) {
      console.log(error.message);
      toastify(error.message, 'error');
    } finally {
      setLoadingMeter(false);
    }
  };

  // Fetch Handover Completion Report
  const fetchCompletionReport = async () => {
    try {
      setLoadingCompletion(true);
      const facilityId = await getItem('selectedFacilityId');
      const params = new URLSearchParams();
      if (completionFilters.search) params.append('search', completionFilters.search);
      params.append('period', completionFilters.period);
      if (completionFilters.status !== 'All Status') params.append('status', completionFilters.status);

      const response = await makeRequest2(
        `${get_handover_completion_report}/${facilityId}?${params.toString()}`,
        'GET',
        {}
      );

      if (response.success) {
        setCompletionData(response.data.data.handovers || []);
        setCompletionSummary(response.data.data.summary || {
          completed: 0,
          draft: 0,
          avgCompletionTime: 0,
          signedCount: 0
        });
      } else {
        throw new Error(response.message || 'Failed to fetch completion report');
      }
    } catch (error) {
      console.log(error.message);
      toastify(error.message, 'error');
    } finally {
      setLoadingCompletion(false);
    }
  };

  // Load initial data on component mount
  useEffect(() => {
    fetchHandoverSummary();
    fetchInventoryComparison();
    fetchMeterReadings();
    fetchCompletionReport();
  }, []);

  const header = (
    <div className="flex align-items-center justify-content-end gap-2">
      <Button type="button" label="CSV" rounded onClick={() => { }} data-pr-tooltip="CSV" />
      <Button type="button" label="XLS" severity="success" rounded onClick={() => { }} data-pr-tooltip="XLS" />
      <Button type="button" label="PDF" severity="warning" rounded onClick={() => { }} data-pr-tooltip="PDF" />
    </div>
  );

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={"/facility/"}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={"/facility/handover_management/handovers"}>Handover Management</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={"/facility/handover_management/reports"}>Reports</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-header">
                  <div className="row">
                    <div className="col-sm-12">
                      <div className="card">
                        <div className="card-body py-0">
                          <ul className="nav nav-tabs profile-tabs" id="myTab" role="tablist">
                            <li className="nav-item">
                              <a className="nav-link active" id="summary-tab" data-bs-toggle="tab" href="#summary" role="tab" aria-selected="true">
                                <i className="ti ti-file-description me-2"></i>Handover Summary
                              </a>
                            </li>
                            <li className="nav-item">
                              <a className="nav-link" id="inventory-tab" data-bs-toggle="tab" href="#inventory" role="tab" aria-selected="false">
                                <i className="ti ti-package me-2"></i>Inventory Comparison
                              </a>
                            </li>
                            <li className="nav-item">
                              <a className="nav-link" id="meter-tab" data-bs-toggle="tab" href="#meter" role="tab" aria-selected="false">
                                <i className="ti ti-gauge me-2"></i>Meter Readings
                              </a>
                            </li>
                            <li className="nav-item">
                              <a className="nav-link" id="completion-tab" data-bs-toggle="tab" href="#completion" role="tab" aria-selected="false">
                                <i className="ti ti-checklist me-2"></i>Completion Metrics
                              </a>
                            </li>
                          </ul>
                        </div>
                      </div>

                      <div className="tab-content">
                        {/* 1. HANDOVER SUMMARY REPORT */}
                        <div className="tab-pane show active" id="summary" role="tabpanel" aria-labelledby="summary-tab">
                          <div className="row">
                            <div className="col-lg-12">
                              <div className="card">
                                <div className="card-header">
                                  <h5>Handover Summary Report</h5>
                                  <small className="text-muted">Complete overview of all handovers with detailed information</small>
                                  <div className="row mt-3">
                                    <div className="col-md-2">
                                      <input
                                        className="form-control"
                                        placeholder="Search unit, customer..."
                                        value={summaryFilters.search}
                                        onChange={(e) => setSummaryFilters({ ...summaryFilters, search: e.target.value })}
                                      />
                                    </div>
                                    <div className="col-md-2">
                                      <select
                                        className="form-control"
                                        value={summaryFilters.handoverType}
                                        onChange={(e) => setSummaryFilters({ ...summaryFilters, handoverType: e.target.value })}
                                      >
                                        <option>All Types</option>
                                        <option>MoveIn</option>
                                        <option>MoveOut</option>
                                      </select>
                                    </div>
                                    <div className="col-md-2">
                                      <select
                                        className="form-control"
                                        value={summaryFilters.status}
                                        onChange={(e) => setSummaryFilters({ ...summaryFilters, status: e.target.value })}
                                      >
                                        <option>All Status</option>
                                        <option>Draft</option>
                                        <option>Completed</option>
                                        <option>Disputed</option>
                                      </select>
                                    </div>
                                    <div className="col-md-2">
                                      <input
                                        className="form-control"
                                        type="date"
                                        value={summaryFilters.startDate}
                                        onChange={(e) => setSummaryFilters({ ...summaryFilters, startDate: e.target.value })}
                                        placeholder="Start Date"
                                      />
                                    </div>
                                    <div className="col-md-2">
                                      <input
                                        className="form-control"
                                        type="date"
                                        value={summaryFilters.endDate}
                                        onChange={(e) => setSummaryFilters({ ...summaryFilters, endDate: e.target.value })}
                                        placeholder="End Date"
                                      />
                                    </div>
                                    <div className="col-md-2">
                                      <button
                                        className="btn btn-primary w-100"
                                        onClick={fetchHandoverSummary}
                                        disabled={loadingSummary}
                                      >
                                        <i className="ti ti-refresh me-2"></i>
                                        {loadingSummary ? 'Loading...' : 'Generate'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div className="card-body">
                                  {/* Summary Cards */}
                                  <div className="row mb-3">
                                    <div className="col-md-3">
                                      <div className="card bg-light-primary">
                                        <div className="card-body">
                                          <h6 className="mb-1">Total Handovers</h6>
                                          <h4 className="mb-0">{summarySummary.totalHandovers}</h4>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-3">
                                      <div className="card bg-light-success">
                                        <div className="card-body">
                                          <h6 className="mb-1">Move-In Handovers</h6>
                                          <h4 className="mb-0">{summarySummary.moveInCount}</h4>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-3">
                                      <div className="card bg-light-info">
                                        <div className="card-body">
                                          <h6 className="mb-1">Move-Out Handovers</h6>
                                          <h4 className="mb-0">{summarySummary.moveOutCount}</h4>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-3">
                                      <div className="card bg-light-warning">
                                        <div className="card-body">
                                          <h6 className="mb-1">Completed</h6>
                                          <h4 className="mb-0">{summarySummary.completedCount}</h4>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <DataTable
                                    value={summaryData}
                                    header={header}
                                    emptyMessage="No handovers found."
                                    sortMode="multiple"
                                    paginator
                                    rows={10}
                                    stripedRows
                                    tableStyle={{ minWidth: '50rem' }}
                                    loading={loadingSummary}
                                  >
                                    <Column field="unitNumber" sortable header="Unit #"></Column>
                                    <Column field="customer" sortable header="Customer"></Column>
                                    <Column field="handoverType" sortable header="Type" body={(rowData) => (
                                      <Tag
                                        value={rowData.handoverType === 'MoveIn' ? 'Move-In' : 'Move-Out'}
                                        severity={rowData.handoverType === 'MoveIn' ? 'success' : 'info'}
                                      />
                                    )}></Column>
                                    <Column field="handoverDate" sortable header="Date" body={(rowData) => new Date(rowData.handoverDate).toLocaleDateString()}></Column>
                                    <Column field="status" sortable header="Status" body={(rowData) => (
                                      <Tag
                                        value={rowData.status}
                                        severity={
                                          rowData.status === 'Completed' ? 'success' :
                                            rowData.status === 'Draft' ? 'warning' :
                                              rowData.status === 'Disputed' ? 'danger' : 'secondary'
                                        }
                                      />
                                    )}></Column>
                                    <Column field="keysHandedOver" header="Keys"></Column>
                                    <Column field="itemsCount" header="Items"></Column>
                                    <Column field="signedByPM" header="PM Signed" body={(rowData) => (
                                      <i className={`ti ${rowData.signedByPM ? 'ti-check text-success' : 'ti-x text-danger'}`}></i>
                                    )}></Column>
                                    <Column field="signedByCustomer" header="Customer Signed" body={(rowData) => (
                                      <i className={`ti ${rowData.signedByCustomer ? 'ti-check text-success' : 'ti-x text-danger'}`}></i>
                                    )}></Column>
                                  </DataTable>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 2. INVENTORY COMPARISON REPORT */}
                        <div className="tab-pane" id="inventory" role="tabpanel" aria-labelledby="inventory-tab">
                          <div className="row">
                            <div className="col-lg-12">
                              <div className="card">
                                <div className="card-header">
                                  <h5>Inventory Comparison Report</h5>
                                  <small className="text-muted">Compare move-in and move-out inventory to identify damages and missing items</small>
                                  <div className="row mt-3">
                                    <div className="col-md-4">
                                      <input
                                        className="form-control"
                                        placeholder="Search unit, item..."
                                        value={inventoryFilters.search}
                                        onChange={(e) => setInventoryFilters({ ...inventoryFilters, search: e.target.value })}
                                      />
                                    </div>
                                    <div className="col-md-3">
                                      <select
                                        className="form-control"
                                        value={inventoryFilters.conditionChange}
                                        onChange={(e) => setInventoryFilters({ ...inventoryFilters, conditionChange: e.target.value })}
                                      >
                                        <option>All Changes</option>
                                        <option>Damaged</option>
                                        <option>Missing</option>
                                        <option>Degraded</option>
                                      </select>
                                    </div>
                                    <div className="col-md-3">
                                      <select
                                        className="form-control"
                                        value={inventoryFilters.category}
                                        onChange={(e) => setInventoryFilters({ ...inventoryFilters, category: e.target.value })}
                                      >
                                        <option>All Categories</option>
                                        <option>Furniture</option>
                                        <option>Appliance</option>
                                        <option>Electronics</option>
                                        <option>Fixture</option>
                                      </select>
                                    </div>
                                    <div className="col-md-2">
                                      <button
                                        className="btn btn-primary w-100"
                                        onClick={fetchInventoryComparison}
                                        disabled={loadingInventory}
                                      >
                                        <i className="ti ti-refresh me-2"></i>
                                        {loadingInventory ? 'Loading...' : 'Generate'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div className="card-body">
                                  {/* Summary Cards */}
                                  <div className="row mb-3">
                                    <div className="col-md-3">
                                      <div className="card bg-light-primary">
                                        <div className="card-body">
                                          <h6 className="mb-1">Total Comparisons</h6>
                                          <h4 className="mb-0">{inventorySummary.totalComparisons}</h4>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-3">
                                      <div className="card bg-light-danger">
                                        <div className="card-body">
                                          <h6 className="mb-1">Damaged Items</h6>
                                          <h4 className="mb-0">{inventorySummary.damaged}</h4>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-3">
                                      <div className="card bg-light-warning">
                                        <div className="card-body">
                                          <h6 className="mb-1">Missing Items</h6>
                                          <h4 className="mb-0">{inventorySummary.missing}</h4>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-3">
                                      <div className="card bg-light-info">
                                        <div className="card-body">
                                          <h6 className="mb-1">Total Cost Impact</h6>
                                          <h4 className="mb-0">KSh {inventorySummary.totalCost?.toLocaleString()}</h4>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <DataTable
                                    value={inventoryData}
                                    header={header}
                                    emptyMessage="No inventory comparisons found."
                                    sortMode="multiple"
                                    paginator
                                    rows={10}
                                    stripedRows
                                    tableStyle={{ minWidth: '50rem' }}
                                    loading={loadingInventory}
                                  >
                                    <Column field="unitNumber" sortable header="Unit #"></Column>
                                    <Column field="customer" sortable header="Customer"></Column>
                                    <Column field="itemName" sortable header="Item"></Column>
                                    <Column field="category" header="Category"></Column>
                                    <Column field="moveInCondition" header="Move-In" body={(rowData) => (
                                      <Tag value={rowData.moveInCondition} severity="success" />
                                    )}></Column>
                                    <Column field="moveOutCondition" header="Move-Out" body={(rowData) => (
                                      <Tag
                                        value={rowData.moveOutCondition}
                                        severity={
                                          rowData.moveOutCondition === 'Excellent' || rowData.moveOutCondition === 'Good' ? 'success' :
                                            rowData.moveOutCondition === 'Fair' ? 'warning' : 'danger'
                                        }
                                      />
                                    )}></Column>
                                    <Column field="change" sortable header="Change" body={(rowData) => (
                                      <Tag
                                        value={rowData.change}
                                        severity={rowData.change === 'No Change' ? 'success' : 'danger'}
                                      />
                                    )}></Column>
                                    <Column field="cost" sortable header="Cost" body={(rowData) => `KSh ${rowData.cost?.toLocaleString()}`}></Column>
                                    <Column field="moveOutDate" sortable header="Move-Out Date" body={(rowData) => new Date(rowData.moveOutDate).toLocaleDateString()}></Column>
                                  </DataTable>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 3. METER READINGS REPORT */}
                        <div className="tab-pane" id="meter" role="tabpanel" aria-labelledby="meter-tab">
                          <div className="row">
                            <div className="col-lg-12">
                              <div className="card">
                                <div className="card-header">
                                  <h5>Meter Readings Report</h5>
                                  <small className="text-muted">Track utility meter readings across all handovers</small>
                                  <div className="row mt-3">
                                    <div className="col-md-3">
                                      <input
                                        className="form-control"
                                        placeholder="Search unit, customer..."
                                        value={meterFilters.search}
                                        onChange={(e) => setMeterFilters({ ...meterFilters, search: e.target.value })}
                                      />
                                    </div>
                                    <div className="col-md-2">
                                      <select
                                        className="form-control"
                                        value={meterFilters.meterType}
                                        onChange={(e) => setMeterFilters({ ...meterFilters, meterType: e.target.value })}
                                      >
                                        <option>All Types</option>
                                        <option>Electricity</option>
                                        <option>Water</option>
                                        <option>Gas</option>
                                      </select>
                                    </div>
                                    <div className="col-md-2">
                                      <input
                                        className="form-control"
                                        type="date"
                                        value={meterFilters.startDate}
                                        onChange={(e) => setMeterFilters({ ...meterFilters, startDate: e.target.value })}
                                      />
                                    </div>
                                    <div className="col-md-2">
                                      <input
                                        className="form-control"
                                        type="date"
                                        value={meterFilters.endDate}
                                        onChange={(e) => setMeterFilters({ ...meterFilters, endDate: e.target.value })}
                                      />
                                    </div>
                                    <div className="col-md-3">
                                      <button
                                        className="btn btn-primary w-100"
                                        onClick={fetchMeterReadings}
                                        disabled={loadingMeter}
                                      >
                                        <i className="ti ti-refresh me-2"></i>
                                        {loadingMeter ? 'Loading...' : 'Generate'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div className="card-body">
                                  {/* Summary Cards */}
                                  <div className="row mb-3">
                                    <div className="col-md-3">
                                      <div className="card bg-light-primary">
                                        <div className="card-body">
                                          <h6 className="mb-1">Total Readings</h6>
                                          <h4 className="mb-0">{meterSummary.totalReadings}</h4>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-3">
                                      <div className="card bg-light-warning">
                                        <div className="card-body">
                                          <h6 className="mb-1">Electricity Readings</h6>
                                          <h4 className="mb-0">{meterSummary.electricity}</h4>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-3">
                                      <div className="card bg-light-info">
                                        <div className="card-body">
                                          <h6 className="mb-1">Water Readings</h6>
                                          <h4 className="mb-0">{meterSummary.water}</h4>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-3">
                                      <div className="card bg-light-success">
                                        <div className="card-body">
                                          <h6 className="mb-1">Gas Readings</h6>
                                          <h4 className="mb-0">{meterSummary.gas}</h4>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <DataTable
                                    value={meterData}
                                    header={header}
                                    emptyMessage="No meter readings found."
                                    sortMode="multiple"
                                    paginator
                                    rows={10}
                                    stripedRows
                                    tableStyle={{ minWidth: '50rem' }}
                                    loading={loadingMeter}
                                  >
                                    <Column field="unitNumber" sortable header="Unit #"></Column>
                                    <Column field="customer" sortable header="Customer"></Column>
                                    <Column field="handoverType" header="Type" body={(rowData) => (
                                      <Tag
                                        value={rowData.handoverType === 'MoveIn' ? 'Move-In' : 'Move-Out'}
                                        severity={rowData.handoverType === 'MoveIn' ? 'success' : 'info'}
                                      />
                                    )}></Column>
                                    <Column field="handoverDate" sortable header="Date" body={(rowData) => new Date(rowData.handoverDate).toLocaleDateString()}></Column>
                                    <Column field="electricityReading" sortable header="Electricity (kWh)"></Column>
                                    <Column field="waterReading" sortable header="Water (m³)"></Column>
                                    <Column field="gasReading" sortable header="Gas (m³)"></Column>
                                  </DataTable>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 4. HANDOVER COMPLETION METRICS */}
                        <div className="tab-pane" id="completion" role="tabpanel" aria-labelledby="completion-tab">
                          <div className="row">
                            <div className="col-lg-12">
                              <div className="card">
                                <div className="card-header">
                                  <h5>Handover Completion Metrics</h5>
                                  <small className="text-muted">Track completion rates, timing, and signature status</small>
                                  <div className="row mt-3">
                                    <div className="col-md-4">
                                      <input
                                        className="form-control"
                                        placeholder="Search unit, customer..."
                                        value={completionFilters.search}
                                        onChange={(e) => setCompletionFilters({ ...completionFilters, search: e.target.value })}
                                      />
                                    </div>
                                    <div className="col-md-3">
                                      <select
                                        className="form-control"
                                        value={completionFilters.period}
                                        onChange={(e) => setCompletionFilters({ ...completionFilters, period: e.target.value })}
                                      >
                                        <option>Last 30 Days</option>
                                        <option>Last 60 Days</option>
                                        <option>Last 90 Days</option>
                                        <option>This Year</option>
                                      </select>
                                    </div>
                                    <div className="col-md-2">
                                      <select
                                        className="form-control"
                                        value={completionFilters.status}
                                        onChange={(e) => setCompletionFilters({ ...completionFilters, status: e.target.value })}
                                      >
                                        <option>All Status</option>
                                        <option>Completed</option>
                                        <option>Draft</option>
                                      </select>
                                    </div>
                                    <div className="col-md-3">
                                      <button
                                        className="btn btn-primary w-100"
                                        onClick={fetchCompletionReport}
                                        disabled={loadingCompletion}
                                      >
                                        <i className="ti ti-refresh me-2"></i>
                                        {loadingCompletion ? 'Loading...' : 'Generate'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div className="card-body">
                                  {/* Summary Cards */}
                                  <div className="row mb-3">
                                    <div className="col-md-3">
                                      <div className="card bg-light-success">
                                        <div className="card-body">
                                          <h6 className="mb-1">Completed</h6>
                                          <h4 className="mb-0">{completionSummary.completed}</h4>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-3">
                                      <div className="card bg-light-warning">
                                        <div className="card-body">
                                          <h6 className="mb-1">Draft</h6>
                                          <h4 className="mb-0">{completionSummary.draft}</h4>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-3">
                                      <div className="card bg-light-info">
                                        <div className="card-body">
                                          <h6 className="mb-1">Avg Completion Time</h6>
                                          <h4 className="mb-0">{completionSummary.avgCompletionTime} hrs</h4>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-3">
                                      <div className="card bg-light-primary">
                                        <div className="card-body">
                                          <h6 className="mb-1">Fully Signed</h6>
                                          <h4 className="mb-0">{completionSummary.signedCount}</h4>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <DataTable
                                    value={completionData}
                                    header={header}
                                    emptyMessage="No completion data found."
                                    sortMode="multiple"
                                    paginator
                                    rows={10}
                                    stripedRows
                                    tableStyle={{ minWidth: '50rem' }}
                                    loading={loadingCompletion}
                                  >
                                    <Column field="unitNumber" sortable header="Unit #"></Column>
                                    <Column field="customer" sortable header="Customer"></Column>
                                    <Column field="handoverType" header="Type" body={(rowData) => (
                                      <Tag
                                        value={rowData.handoverType === 'MoveIn' ? 'Move-In' : 'Move-Out'}
                                        severity={rowData.handoverType === 'MoveIn' ? 'success' : 'info'}
                                      />
                                    )}></Column>
                                    <Column field="createdDate" sortable header="Created" body={(rowData) => new Date(rowData.createdDate).toLocaleDateString()}></Column>
                                    <Column field="completedDate" sortable header="Completed" body={(rowData) =>
                                      rowData.completedDate ? new Date(rowData.completedDate).toLocaleDateString() : '-'
                                    }></Column>
                                    <Column field="completionTime" sortable header="Time (hrs)"></Column>
                                    <Column field="status" sortable header="Status" body={(rowData) => (
                                      <Tag
                                        value={rowData.status}
                                        severity={rowData.status === 'Completed' ? 'success' : 'warning'}
                                      />
                                    )}></Column>
                                    <Column field="pmSigned" header="PM Signed" body={(rowData) => (
                                      <i className={`ti ${rowData.pmSigned ? 'ti-check text-success' : 'ti-x text-danger'}`}></i>
                                    )}></Column>
                                    <Column field="customerSigned" header="Customer Signed" body={(rowData) => (
                                      <i className={`ti ${rowData.customerSigned ? 'ti-check text-success' : 'ti-x text-danger'}`}></i>
                                    )}></Column>
                                  </DataTable>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default HandoverReports;
